import { getHlsFromVideoJsPlayer } from '@app/core/video-player/video-js/hls-playback-fix/hls-pdt-playback';
import { RefObject, useEffect, useRef } from 'react';
import Player from 'video.js/dist/types/player';
import { addHlsTimelineListeners } from './add-hls-timeline-listeners';
import { TimelineChart } from './timeline-chart';

/**
 * Creates and maintains an HLS timeline chart for a Video.js player with hls.js attached.
 * Handles resize, event subscription, and cleanup on player dispose.
 * Returns a ref to the chart instance for imperative actions (e.g. reset zoom).
 */
export function useHlsTimelineChart(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  containerRef: RefObject<HTMLDivElement | null>,
  player: Player | null,
) {
  const chartRef = useRef<TimelineChart | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!player || player.isDisposed() || !canvas) {
      return;
    }

    const hls = getHlsFromVideoJsPlayer(player);
    if (!hls) {
      return;
    }

    const chart = new TimelineChart(canvas, hls, { responsive: false });
    chartRef.current = chart;
    chart.show();

    const removeListeners = addHlsTimelineListeners(hls, chart);

    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    chart.resize();

    return () => {
      removeListeners();
      resizeObserver.disconnect();
      chartRef.current = null;
      chart.destroy();
    };
  }, [canvasRef, containerRef, player]);

  return chartRef;
}
